import React, { useState, useEffect } from 'react';
import {
    Box, Container, Typography, Grid, Card, Button,
    alpha, Stack, Chip, Avatar, Divider, Paper, Fade, Grow,
} from '@mui/material';
import {
    School as SchoolIcon,
    LocationOn as LocationIcon,
    CheckCircle as CheckIcon,
    MenuBook as BookIcon,
    EmojiEvents as TrophyIcon,
    Groups as GroupsIcon,
    LaptopMac as LmsIcon,
    CloudUpload as UploadIcon,
    Assignment as CourseIcon,
    Person as PersonIcon,
    Security as SecurityIcon,
    School as CertIcon,
    Dashboard as DashboardIcon,
    ArrowBack as ArrowBackIcon,
    Phone as PhoneIcon,
    Email as EmailIcon,
    Engineering as EngineeringIcon,
    Build as BuildIcon,
    Apartment as FacilityIcon,
    BusinessCenter as BusinessIcon,
    AdminPanelSettings as AdminIcon,
    Hotel as HotelIcon,
    Restaurant as FoodIcon,
    Computer as ComputerIcon,
    Architecture as ArchIcon,
    Construction as ConstructionIcon,
    ElectricalServices as BuildingServicesIcon,
    Public as InternationalIcon,
    ViewModule as ModuleIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Navbar from '../Navbar';
import HomeFooter from '../HomeFooter';

const BRAND_GRADIENT = 'linear-gradient(135deg, #1d748b 0%, #52d370 100%)';
const TRENT_COLOR = '#dc3545';

// ==================== TRENT DATA ====================
const trentData = {
    name: 'Trent International Institute',
    shortName: 'TIC',
    tagline: 'Global Learning, Local Values',
    color: TRENT_COLOR,
    established: '2001',
    location: 'Singapore',
    type: 'Higher Education & Digital Learning Platform',
    about: 'Trent International Institute is a Singapore-based education institution focused on professional and higher education programmes, with academic offerings covering technology, built environment, business, hospitality and related disciplines.',
    lmsIntro: 'We developed and implemented a dedicated Learning Management System (LMS) for Trent International Institute to provide a centralized digital learning environment for students, courses and academic resources.',

    // LMS Solutions
    lmsSolutions: [
        {
            title: 'Learning Management',
            desc: 'Centralized platform for digital learning and academic activities.',
            icon: <LmsIcon />,
        },
        {
            title: 'Course Management',
            desc: 'Organizes programmes, courses and module content.',
            icon: <CourseIcon />,
        },
        {
            title: 'Student Portal',
            desc: 'Provides students with a centralized online learning environment.',
            icon: <PersonIcon />,
        },
        {
            title: 'Digital Learning Resources',
            desc: 'Enables access to course materials and educational resources.',
            icon: <BookIcon />,
        },
        {
            title: 'Academic Management',
            desc: 'Supports structured management of courses and learning activities.',
            icon: <DashboardIcon />,
        },
        {
            title: 'Assessment Management',
            desc: 'Supports coursework, assessments and academic activities.',
            icon: <UploadIcon />,
        },
        {
            title: 'Module Management',
            desc: 'Organizes individual modules and their associated learning content.',
            icon: <ModuleIcon />,
        },
        {
            title: 'Online Learning',
            desc: 'Supports digital and online components of academic programmes.',
            icon: <CertIcon />,
        },
        {
            title: 'Student Academic Services',
            desc: 'Provides a centralized environment for student-related academic activities.',
            icon: <GroupsIcon />,
        },
    ],

    // Key Academic Areas with icons
    academicAreas: [
        { name: 'Built Environment', icon: <ArchIcon /> },
        { name: 'Construction Management', icon: <ConstructionIcon /> },
        { name: 'Building Services', icon: <BuildingServicesIcon /> },
        { name: 'Engineering', icon: <EngineeringIcon /> },
        { name: 'Technology', icon: <BuildIcon /> },
        { name: 'Computer Science', icon: <ComputerIcon /> },
        { name: 'Business Management', icon: <BusinessIcon /> },
        { name: 'International Business', icon: <InternationalIcon /> },
        { name: 'Hospitality & Tourism', icon: <HotelIcon /> },
        { name: 'Food Technology & Catering', icon: <FoodIcon /> },
    ],

    // Quick info
    quickInfo: [
        { label: 'Client', value: 'Trent International Institute', icon: <SchoolIcon /> },
        // { label: 'Location', value: 'Singapore', icon: <LocationIcon /> },
        { label: 'Solution', value: 'Learning Management System (LMS)', icon: <LmsIcon /> },
        { label: 'Platform', value: 'Trent International Institute LMS', icon: <DashboardIcon /> },
    ],

    // Technologies
    technologies: [
        'Learning Management System (LMS)',
        'Student Portal',
        'Course Management',
        'Module Management',
        'Digital Education',
        'Academic Management',
        'Assessment Management',
        'Online Learning',
    ],
};

const TrentCollegePage = () => {
    const [mounted, setMounted] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        setMounted(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    if (!mounted) return null;

    return (
        <Box sx={{ width: '100%', minHeight: '100vh', bgcolor: '#f8f9fa' }}>
            <Navbar />

            {/* ==================== HERO ==================== */}
            <Box
                sx={{
                    background: BRAND_GRADIENT,
                    pt: { xs: 8, md: 10 },
                    pb: { xs: 12, md: 16 },
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <Box sx={{ position: 'absolute', top: '10%', right: '5%', width: 350, height: 350, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.25) 0%, transparent 70%)' }} />
                <Box sx={{ position: 'absolute', bottom: '5%', left: '5%', width: 250, height: 250, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)' }} />

                <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
                    {/* Back Button */}
                    {/* <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate('/colleges')}
                        sx={{
                            mb: 3,
                            color: 'white',
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                        }}
                    >
                        Back to Colleges
                    </Button> */}

                    <Fade in timeout={800}>
                        <Box>
                            {/* <Chip
                                label="CLIENT PROJECT"
                                sx={{
                                    mb: 3,
                                    bgcolor: 'rgba(255,255,255,0.2)',
                                    color: 'white',
                                    fontWeight: 700,
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255,255,255,0.3)',
                                }}
                            /> */}

                            <Typography
                                variant="h1"
                                sx={{
                                    fontSize: { xs: '2.2rem', md: '3.5rem' },
                                    fontWeight: 800,
                                    mb: 2,
                                    color: 'white',
                                    lineHeight: 1.2,
                                }}
                            >
                                {trentData.name}
                            </Typography>

                            <Typography
                                variant="h6"
                                sx={{
                                    color: 'rgba(255,255,255,0.95)',
                                    maxWidth: 800,
                                    fontWeight: 400,
                                    mb: 3,
                                }}
                            >
                                {trentData.type}
                            </Typography>

                            <Stack direction="row" spacing={2} flexWrap="wrap">
                                {/* <Chip
                                    icon={<LocationIcon sx={{ color: 'white !important' }} />}
                                    label="Singapore"
                                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }}
                                /> */}
                                <Chip
                                    icon={<LmsIcon sx={{ color: 'white !important' }} />}
                                    label="LMS Platform"
                                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }}
                                />
                                <Chip
                                    icon={<SchoolIcon sx={{ color: 'white !important' }} />}
                                    label="Professional & Higher Education"
                                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }}
                                />
                            </Stack>
                        </Box>
                    </Fade>
                </Container>
            </Box>

            {/* ==================== OVERVIEW CARD ==================== */}
            <Container maxWidth="xl" sx={{ mt: -10, mb: 5, position: 'relative', zIndex: 2 }}>
                <Grow in timeout={1000}>
                    <Paper elevation={24} sx={{ p: { xs: 3, md: 5 }, borderRadius: 4, bgcolor: 'white' }}>
                        <Grid container spacing={4}>
                            <Grid item xs={12} md={8}>
                                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                                    <Avatar
                                        sx={{
                                            width: 70,
                                            height: 70,
                                            background: TRENT_COLOR,
                                            fontWeight: 800,
                                            fontSize: '1.5rem',
                                        }}
                                    >
                                        {trentData.shortName}
                                    </Avatar>
                                    <Box>
                                        <Typography variant="h4" sx={{ fontWeight: 800 }}>
                                            {trentData.name}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {trentData.type}
                                        </Typography>
                                    </Box>
                                </Stack>

                                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                                    {trentData.about}
                                </Typography>

                                <Typography variant="body1" color="text.secondary">
                                    {trentData.lmsIntro}
                                </Typography>
                            </Grid>

                            {/* Quick Info */}
                            <Grid item xs={12} md={4}>
                                <Stack spacing={2}>
                                    {trentData.quickInfo.map((info) => (
                                        <Paper
                                            key={info.label}
                                            sx={{ p: 2, bgcolor: alpha(TRENT_COLOR, 0.08), borderRadius: 2 }}
                                        >
                                            <Stack direction="row" spacing={2} alignItems="center">
                                                <Box sx={{ color: TRENT_COLOR }}>{info.icon}</Box>
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {info.label}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                        {info.value}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </Paper>
                                    ))}
                                </Stack>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grow>
            </Container>

            {/* ==================== LMS SOLUTIONS ==================== */}
            <Container maxWidth="xl" sx={{ py: 4 }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                    <LmsIcon sx={{ color: TRENT_COLOR, fontSize: 36 }} />
                    <Typography variant="h4" sx={{ fontWeight: 800 }}>
                        LMS Solutions
                    </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                    Key modules we developed for the Trent International Institute learning platform
                </Typography>

                <Grid container spacing={3}>
                    {trentData.lmsSolutions.map((solution, i) => (
                        <Grid item xs={12} sm={6} md={4} key={solution.title}>
                            <Grow in timeout={(i + 1) * 80}>
                                <Card
                                    sx={{
                                        p: 3,
                                        borderRadius: 3,
                                        height: '100%',
                                        boxShadow: '0 8px 30px rgba(0,0,0,0.06)',
                                        transition: 'all 0.25s',
                                        '&:hover': {
                                            transform: 'translateY(-6px)',
                                            boxShadow: `0 20px 40px ${alpha(TRENT_COLOR, 0.2)}`,
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 52,
                                            height: 52,
                                            borderRadius: 2,
                                            bgcolor: alpha(TRENT_COLOR, 0.12),
                                            color: TRENT_COLOR,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mb: 2,
                                        }}
                                    >
                                        {solution.icon}
                                    </Box>
                                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                                        {solution.title}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {solution.desc}
                                    </Typography>
                                </Card>
                            </Grow>
                        </Grid>
                    ))}
                </Grid>
            </Container>

            {/* ==================== KEY ACADEMIC AREAS ==================== */}
            <Box sx={{ bgcolor: '#f8f9fa', py: 6 }}>
                <Container maxWidth="xl">
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                        <SchoolIcon sx={{ color: TRENT_COLOR, fontSize: 36 }} />
                        <Typography variant="h4" sx={{ fontWeight: 800 }}>
                            Key Academic Areas
                        </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                        Professional and higher education disciplines offered at Trent International Institute
                    </Typography>

                    <Grid container spacing={3}>
                        {trentData.academicAreas.map((area, i) => (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={area.name}>
                                <Grow in timeout={(i + 1) * 80}>
                                    <Card
                                        sx={{
                                            p: 3,
                                            borderRadius: 3,
                                            height: '100%',
                                            boxShadow: '0 8px 30px rgba(0,0,0,0.06)',
                                            borderLeft: `4px solid ${TRENT_COLOR}`,
                                            transition: 'all 0.25s',
                                            '&:hover': {
                                                transform: 'translateY(-6px)',
                                                boxShadow: `0 20px 40px ${alpha(TRENT_COLOR, 0.2)}`,
                                            },
                                        }}
                                    >
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <Avatar
                                                sx={{
                                                    bgcolor: alpha(TRENT_COLOR, 0.12),
                                                    color: TRENT_COLOR,
                                                    width: 44,
                                                    height: 44,
                                                }}
                                            >
                                                {area.icon}
                                            </Avatar>
                                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                                {area.name}
                                            </Typography>
                                        </Stack>
                                    </Card>
                                </Grow>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* ==================== TECHNOLOGY & SOLUTION ==================== */}
            <Container maxWidth="xl" sx={{ py: 6 }}>
                <Paper sx={{ p: 4, borderRadius: 3 }}>
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                        <SecurityIcon sx={{ color: TRENT_COLOR, fontSize: 32 }} />
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                            Technology & Solution
                        </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Technologies and solutions used to build the Trent LMS platform
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {trentData.technologies.map((tech) => (
                            <Chip
                                key={tech}
                                label={tech}
                                sx={{
                                    bgcolor: alpha(TRENT_COLOR, 0.12),
                                    color: TRENT_COLOR,
                                    fontWeight: 600,
                                    mb: 1,
                                }}
                            />
                        ))}
                    </Stack>
                </Paper>
            </Container>

            {/* ==================== CTA ==================== */}


        </Box>
    );
};

export default TrentCollegePage;